import type { SpanOptions } from '@opentelemetry/api';
import { trace, context, SpanStatusCode, SpanKind, TraceFlags } from '@opentelemetry/api';
import type { Middleware, Dispatch, UnknownAction, Store } from 'redux';

import { setSemanticAttributes, getParentSpan } from './trace';

/**
 * Creates a collection of traces to mark transactions between rtk and store
 */
const tracer = trace.getTracer('rtk-tracer');
const unknownType = 'rtk-action-without-type';
// Deny list avoids noisy spans, though could be added as events on a parent. 🔮
const noSpanners = [
    'internal_getRTKQSubscriptions',
    'internalSubscriptions',
    'middlewareRegistered',
    'persist/PERSIST',
    'persist/REHYDRATE',
    'removeQueryResult',
    'removeMutationResult',
    'tasks/updateTask',
    unknownType,
    'unsubscribeQueryResult',
];

/**
 * Apps will need to add this spanMiddleware to the store middleware to initialize
 * a tracer that makes spans for transactions between `rtk` actions & _store.
 * Currently has a deny list that might be better as an allow list.  ✂️
 * The middleware spans @timestamp the filtered store events (e.g reset, pending,
 * fulfilled, rejected, etc) as a descendant or at least w/ a link
 * @requires TraceProvider
 * @param _store
 * @returns unknown
 */
const spanMiddleware =
    (_store: Store) => (next: Dispatch<UnknownAction>) => (action: UnknownAction) => {
        // Start a new span for the Redux action with the action type as the span name
        const spanName = `${(action as any)?.type ?? unknownType}`;

        for (const skip of noSpanners) {
            if (spanName.includes(skip)) {
                return next(action);
            }
        }

        const parentSpan = getParentSpan();
        const spanContext = parentSpan?.spanContext();
        const traceFlags = TraceFlags.SAMPLED;
        const links = spanContext ? [{ context: { ...spanContext, traceFlags } }] : undefined;
        const spanOptions: SpanOptions = {
            kind: SpanKind.SERVER,
            attributes: {
                'http.scheme': 'render',
                'http.pathname': spanName,
            },
            links,
        };
        // Creates a transaction via a span with the env & browser metric
        const currentSpan = tracer.startSpan(spanName, spanOptions);
        setSemanticAttributes(currentSpan);

        const { setSpan } = trace;
        return context.with(setSpan(context.active(), currentSpan), () => {
            // Make use of rtk query or mutation ReturnValue meta object w/ unknown type
            const actionMeta = action?.meta;
            if (actionMeta && typeof actionMeta === 'object') {
                const endpointName = (actionMeta as any)?.arg?.endpointName;
                if (endpointName) {
                    // Add the endpointName for better correlation
                    currentSpan.updateName(`${endpointName}-${spanName}`);
                }

                const originalArgs = (actionMeta as any)?.arg?.originalArgs;
                if (typeof originalArgs === 'object') {
                    const type = originalArgs?.type ?? 'query';
                    const attributes = {};
                    for (const [key, value] of Object.entries(originalArgs)) {
                        if (typeof value !== 'undefined') {
                            const semanticKey = `${endpointName}.${type}.${key}`;
                            Object.assign(attributes, { [semanticKey]: value });
                        }
                    }
                    currentSpan.setAttributes(attributes);
                }
            }
            try {
                // Dispatch the action and get the result
                const result = next(action);
                currentSpan.setStatus({ code: SpanStatusCode.OK });
                return result;
            } catch (error: any) {
                // Record any exceptions
                currentSpan.setStatus({ code: SpanStatusCode.ERROR });
                currentSpan.recordException(error);
                throw error;
            } finally {
                // End the span after the action is processed
                currentSpan.end();
            }
        });
    };

export default spanMiddleware as unknown as Middleware;
